import { useIsMutating } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import { useEvent } from 'hb-react/shared/apps/Events/hooks';
import {
  type Addon,
  type Attendee,
  type Donation,
  type EventTicket,
} from 'hb-react/shared/apps/Events/types';
import {
  type AddonCartItem,
  type CartItemOf,
  type CartItems,
  type CartItemsAttributes,
  type DonationCartItem,
  type DonationCartItemAttributes,
  type ReceiptAttributes,
  type TicketCartItem,
} from 'hb-react/shared/components/ShoppingCard/hooks/v2';
import { useShoppingCard } from 'hb-react/shared/components/ShoppingCard/hooks/v2';
import { axiosInstance } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import { transformCartItems } from './utils/transformer';

export type TicketItem = CartItemOf<TicketCartItem> & EventTicket;
export type DonationItem = CartItemOf<DonationCartItem> & Donation;
export type AddonItem = Addon & CartItemOf<AddonCartItem>;

export const useBooking = () => {
  const { cart, actions, isLoading, metadata } = useShoppingCard();
  const navigate = useNavigate();

  const { event, isLoading: isEventLoading } = useEvent({
    route: Routes.network_event_registration_path,
  });

  const location = useLocation();
  const { pathname } = useMatch(location.pathname)!;

  const [page] = pathname.split('/').slice(-1);

  useEffect(() => {
    if (cart?.status === 'paid' || cart?.payload?.pay_later) {
      navigate('./success');
    }
  }, [cart?.status, navigate, cart?.payload?.pay_later]);

  const [tickets, attendees, addons, donations] = useMemo<
    [Array<TicketItem>, Array<Attendee>, Array<AddonItem>, Array<DonationItem>]
  >(() => {
    if (!cart || !event) {
      return [[], [], [], []];
    }

    return transformCartItems({ cart, event });
  }, [cart, event]);

  // TODO: Refactor this :melting_face:
  const reset = useCallback(
    ({
      cartItems,
      receipt = {},
      donations = [],
      metadata: meta = {},
    }: {
      cartItems?: Array<CartItems | CartItemsAttributes>;
      donations?: Array<DonationCartItemAttributes>;
      metadata?: { donations_displayed?: boolean; pay_later?: boolean };
      receipt?: ReceiptAttributes;
    }) => {
      const items = cartItems || cart?.cart_items || [];

      const donationsOrPromoCodesItems =
        donations.length > 0
          ? donations
          : (cart?.cart_items || []).filter(({ line_itemable_type: type }) =>
              ['Donation::Campaign', 'Ecommerce::PromoCode'].includes(type),
            );

      const { currency } =
        items.find(
          (item): item is TicketCartItem =>
            item.line_itemable_type === 'NetworkEvents::Ticket' &&
            !item?.metadata?.event?.isFree,
        ) ||
        items?.[0] ||
        cart;

      return actions
        .reset({
          currency,
          cart_items: [
            ...items.filter(
              ({ line_itemable_type: type }) =>
                !['Donation::Campaign', 'Ecommerce::PromoCode'].includes(type),
            ),
            ...donationsOrPromoCodesItems,
          ],
          billing_address_attributes: receipt?.billing_address_attributes,
          shipping_address_attributes: receipt?.shipping_address_attributes,
          user: {
            lastname: receipt.lastname ?? metadata?.user?.lastname ?? '',
            firstname: receipt.firstname ?? metadata?.user?.firstname ?? '',
            email: receipt.email ?? metadata?.user?.email ?? '',
          },
          metadata: {
            donations_displayed: meta?.donations_displayed,
            // eslint-disable-next-line camelcase
            address_type: receipt?.address_type,
            pay_later: meta?.pay_later,
          },
        })
        .catch((e) => {
          if (
            e.response?.data?.error ===
            'OrderManagement::Cart::AlreadyProcessedError'
          ) {
            return navigate('../success');
          }

          errorReporting.captureException(e);

          throw e;
        });
    },
    [actions, cart, metadata, navigate],
  );

  const isMutating = useIsMutating();

  const actionsMemoized = useMemo(
    () => ({
      ...actions,
      payLater: async (values) => {
        const cartItems = cart?.cart_items || [];

        await reset({
          ...values,
          metadata: {
            pay_later: true,
          },
        });

        await axiosInstance.post(
          Routes.pay_later_network_event_attendees_path(event!.id, {
            format: 'json',
          }),
          {
            ...values,
            network_events_booking: {
              donations_attributes: cartItems.filter(
                (item) => item.line_itemable_type === 'Donation::Campaign',
              ),
              attendees_attributes: [
                ...cartItems
                  .filter(
                    (item): item is TicketCartItem =>
                      'line_itemable_type' in item &&
                      item.line_itemable_type === 'NetworkEvents::Ticket',
                  )
                  .map((attendee) => ({
                    ...attendee,
                    ...attendee.metadata.event,
                    sub_attendees: cartItems
                      .filter(
                        (item): item is AddonCartItem =>
                          item.line_itemable_type ===
                            'Ecommerce::Products::EventAddon' &&
                          item.metadata.event.type === 'sub_addon' &&
                          attendee.metadata.event!.uuid ===
                            item.metadata.event.parent_uuid,
                      )
                      .map((addon) => ({
                        addon_id: addon.line_itemable_id,
                        ...addon,
                        ...addon.metadata.event,
                      })),
                  })),
                ...cartItems
                  .filter(
                    (item): item is AddonCartItem =>
                      item.line_itemable_type ===
                        'Ecommerce::Products::EventAddon' &&
                      item.metadata.event.type === 'standalone_addon',
                  )
                  .map((addon) => ({
                    firstname: values?.receipt?.firstname,
                    lastname: values?.receipt?.lastname,
                    email: values?.receipt?.email,
                    addon_id: addon.line_itemable_id,
                    ...addon,
                    ...addon.metadata.event,
                  })),
              ],
            },
          },
        );
      },
      reset,
    }),
    [actions, cart?.cart_items, reset, event],
  );

  return {
    isLoading: isEventLoading || isLoading,
    isMutating: Boolean(isMutating),
    metadata,
    event,
    tickets,
    attendees,
    addons,
    donations,
    page,
    canEditCartItems: page !== 'payment' && page !== 'success',
    cart,
    actions: actionsMemoized,
  };
};
